import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Avatar,
  SelectChangeEvent,
} from "@mui/material";
import { useTenant } from "../contexts/TenantContext";
import { useNavigate } from "react-router-dom";
import facilityService from "../services/facilityService";
import userService from "../services/userService";

interface Facility {
  id: string;
  name: string;
  primaryColor: string;
  logoUrl?: string;
}

// Mock data for tenants - in a real app, this would come from an API
const mockTenants = [
  { id: "Clover Home ", name: "Clover Home ", primaryColor: "#1976d2" },
  {
    id: "Lakewooodvilla",
    name: "Lakewoood Villa Care Center",
    primaryColor: "#7b1fa2",
  },
];

const DEFAULT_LOGO_URL = "/assets/JSNU-logo.png";

interface TenantSelectorProps {
  collapsed?: boolean;
}

const TenantSelector: React.FC<TenantSelectorProps> = ({ collapsed = false }) => {
  const tenantContext = useTenant();
  const navigate = useNavigate();
  const [user] = useState<any>(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [facilities, setFacilities] = useState<Facility[]>([]);

  useEffect(() => {
    if (!user) return;

    const fetchFacilitiesList = async () => {
      try {
        let targetUserId = user?.id;
        
        if (targetUserId) {
          try {
            const dbUser = await userService.getUserById(targetUserId);
            if (dbUser && dbUser.id) {
              targetUserId = dbUser.id;
            }
          } catch (e) {
            console.warn("Could not fetch internal DB user, falling back to context user.id", e);
          }
        }

        let response;
        const userRole = user?.role?.toUpperCase();
        const isSuperAdmin = userRole === "SUPER_ADMIN";
        const isAdmin = userRole === "ADMIN";
        
        if (isSuperAdmin || isAdmin) {
          // Admin and Super Admin: fetch ALL facilities via role-based access
          const allFacilitiesPage = await facilityService.getAllFacilities(0, 100);
          // getAllFacilities returns a PageResponse; extract the content array
          const items: any[] =
            (allFacilitiesPage as any)?.content ??
            (allFacilitiesPage as any)?.items ??
            (Array.isArray(allFacilitiesPage) ? allFacilitiesPage : []);
          response = items;
        } else if (targetUserId) {
          response = await facilityService.getFacilitiesByUser(targetUserId);
        } else {
          response = await facilityService.getActiveFacilities();
        }

        if (response && Array.isArray(response)) {
          let mapped: Facility[] = response.map((f: any) => ({
            id: String(f.id),
            name: f.name || "",
            primaryColor: "#1976d2",
            logoUrl: DEFAULT_LOGO_URL,
          }));
          
          setFacilities(mapped);
        }
      } catch (error) {
        console.error("Failed to fetch facilities from API", error);
      }
    };

    fetchFacilitiesList();

    const handleFacilitiesUpdated = () => fetchFacilitiesList();
    window.addEventListener("facilitiesUpdated", handleFacilitiesUpdated);

    return () => {
      window.removeEventListener("facilitiesUpdated", handleFacilitiesUpdated);
    };
  }, [user]);

  useEffect(() => {
    if (!tenantContext || facilities.length === 0) return;

    const storedTenantId = localStorage.getItem("currentTenantId");
    const validStored = facilities.find((f) => f.id === storedTenantId);

    const selected = validStored ?? facilities[0];

    // Update if the selected tenant is different or if the name needs updating from fallback
    if (selected) {
      const isDifferentId = selected.id !== tenantContext.currentTenantId;
      const isDifferentName = selected.name !== tenantContext.tenant?.name;
      
      if (isDifferentId || isDifferentName) {
        tenantContext.setTenant(selected);
        tenantContext.setCurrentTenantId(selected.id);
        localStorage.setItem("currentTenantId", selected.id);
      }
    }
  }, [facilities, tenantContext.currentTenantId, tenantContext]);

  const handleTenantChange = (event: SelectChangeEvent<string>) => {
    const tenantId = event.target.value as string;
    const selectedTenant = facilities.find((t: Facility) => t.id === tenantId);

    if (selectedTenant && tenantContext) {
      const tenantData = {
        id: selectedTenant.id,
        name: selectedTenant.name,
        primaryColor: selectedTenant.primaryColor,
        logoUrl: selectedTenant.logoUrl,
      };

      tenantContext.setTenant(tenantData);
      tenantContext.setCurrentTenantId(tenantId);
      localStorage.setItem("currentTenantId", tenantId);

      // Navigate to dashboard to ensure all page states are reset for the new tenant
      navigate("/dashboard");
    }
  };

  return (
    <Box sx={{ width: "100%", px: collapsed ? 0 : 2, py: 1, display: "flex", justifyContent: "center" }}>
      <FormControl fullWidth={!collapsed} size="small" sx={collapsed ? { minWidth: 0, '& .MuiInputBase-root': { '& fieldset': { border: 'none' } } } : {}}>
        {!collapsed && <InputLabel id="tenant-select-label">Facility</InputLabel>}
        <Select
          labelId="tenant-select-label"
          id="tenant-select"
          value={tenantContext.currentTenantId || ""}
          label={!collapsed ? "Facility" : undefined}
          onChange={handleTenantChange}
          IconComponent={collapsed ? () => null : undefined}
          sx={collapsed ? { '& .MuiSelect-select': { padding: '8px !important', display: 'flex', justifyContent: 'center' } } : {}}
          MenuProps={{
            PaperProps: {
              sx: { minWidth: 200 }
            }
          }}
        >
          {facilities.map((tenant: Facility) => (
            <MenuItem key={tenant.id} value={tenant.id}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: "0.75rem",
                    bgcolor: tenant.primaryColor || "#1976d2",
                    mr: collapsed ? 0 : 1,
                  }}
                >
                  {tenant.name.charAt(0)}
                </Avatar>
                {!collapsed && <Typography sx={{ fontSize: "13px" }}>{tenant.name.replace(/_/g, " ")}</Typography>}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default TenantSelector;
